import type { SubmitQualificationResponse } from '@nexofacil/schemas';
import { VERTICAL_KEYS } from '@nexofacil/schemas';
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SeoHead } from '@/components/seo-head';
import { api } from '@/lib/api';
import { brokerAvatarClass, type AvatarShape } from '@/lib/broker-avatar';
import { shortBrokerName } from '@/lib/broker-display-name';
import { messages } from '@/messages';

// Tela pós-submit · unificada (não expõe mais status binário pro lead).
// Backend continua qualificando por dentro (broker/CAPI/dedup precisam) ·
// só a UI pública virou única e empática pra evitar frustração do "não
// qualificado".
//
// A tela tem um trabalho só: levar o lead até a conversa no WhatsApp. Por
// isso ela mostra o corretor (foto e nome), não a marca NexoFácil · o lead
// veio da página do corretor e é com ele que vai falar.

export type ResultPageState = SubmitQualificationResponse & {
  broker?: {
    name: string;
    avatarUrl: string | null;
    avatarShape?: AvatarShape;
    verticalKey?: string;
  } | null;
};

export function QualificationResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state as ResultPageState | null;

  useEffect(() => {
    if (!data) navigate('/', { replace: true });
  }, [data, navigate]);

  if (!data) return null;

  const isPv = data.verticalKey === VERTICAL_KEYS.PROTECAO_VEICULAR;
  const broker = data.broker ?? null;
  const shortName = broker ? shortBrokerName(broker.name, broker.verticalKey) : undefined;
  const isIg = data.verticalKey === VERTICAL_KEYS.IMOBILIARIO_GERAL;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SeoHead noindex />
      <ResultHeader broker={broker} />

      <main className="flex-1">
        <div className="container mx-auto max-w-xl px-4 pb-16 pt-10 md:pt-16">
          <div className="animate-fade-in text-center">
            {broker?.avatarUrl ? (
              <div className="relative mx-auto h-20 w-20">
                <img
                  src={broker.avatarUrl}
                  alt={broker.name}
                  className={brokerAvatarClass(broker.avatarShape, 'h-20 w-20 shadow-sm')}
                />
                <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] ring-4 ring-background">
                  <WhatsAppIcon className="h-4 w-4 text-white" />
                </span>
              </div>
            ) : (
              <div
                className={
                  'mx-auto flex h-20 w-20 items-center justify-center rounded-full ' +
                  (isPv ? 'bg-[hsl(var(--brand-accent))]/15' : 'bg-emerald-100')
                }
              >
                {isPv ? (
                  <ShieldCheck
                    className="h-10 w-10 text-[hsl(var(--brand-accent))]"
                    strokeWidth={2.25}
                  />
                ) : (
                  <Sparkles className="h-10 w-10 text-emerald-700" strokeWidth={2.25} />
                )}
              </div>
            )}

            <div className="mt-6 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
              {messages.resultEyebrow}
            </div>
            <h1 className="mt-2 text-balance text-3xl font-extrabold leading-tight md:text-4xl">
              {messages.resultTitle}
            </h1>
            <p className="mx-auto mt-4 max-w-md text-balance text-base text-muted-foreground">
              {isPv ? messages.resultPvBody : messages.resultBody}
            </p>
          </div>

          <div className="mt-10 flex flex-col items-stretch gap-2">
            <Button
              asChild
              size="lg"
              className="h-14 bg-[#25D366] text-base font-semibold text-[#052E16] hover:bg-[#20BC5C] focus-visible:ring-[#25D366]"
            >
              <a
                href={data.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => api.trackWhatsappClick(data.leadId)}
              >
                <WhatsAppIcon className="mr-2 h-5 w-5" />
                {messages.ctaSendWhatsapp(shortName)}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              {messages.resultCtaHint}
            </p>
          </div>

          <NextStepsCard isPv={isPv} isIg={isIg} />

          <p className="mt-10 text-center text-xs leading-relaxed text-muted-foreground">
            {isPv
              ? messages.resultPvDisclaimer
              : isIg
                ? messages.resultIgDisclaimer
                : messages.resultDisclaimer}
          </p>
        </div>
      </main>
    </div>
  );
}

function ResultHeader({ broker }: { broker: ResultPageState['broker'] }) {
  return (
    <header className="border-b bg-background">
      <div className="container flex h-16 items-center gap-2.5">
        {broker ? (
          <>
            {broker.avatarUrl && (
              <img
                src={broker.avatarUrl}
                alt=""
                className={brokerAvatarClass(broker.avatarShape, 'h-8 w-8')}
              />
            )}
            <span className="text-sm font-semibold">{broker.name}</span>
          </>
        ) : (
          <>
            <img src="/nexofacil-icon.png" alt="" className="h-7 w-7" />
            <span className="text-sm font-semibold">NexoFácil</span>
          </>
        )}
      </div>
    </header>
  );
}

// Substitui o DetailsCard antigo (critérios falhados) por passos genéricos.
function NextStepsCard({ isPv, isIg }: { isPv: boolean; isIg: boolean }) {
  const steps = isPv
    ? messages.resultPvNextSteps
    : isIg
      ? messages.resultIgNextSteps
      : messages.resultMcmvNextSteps;
  const title = isPv ? messages.resultPvNextStepsTitle : messages.resultMcmvNextStepsTitle;

  return (
    <div className="mt-8 rounded-xl border bg-card p-5">
      <p className="text-sm font-semibold">{title}</p>
      <ul className="mt-3 space-y-2.5">
        {steps.map((step) => (
          <li key={step} className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span className="text-sm leading-relaxed text-foreground">{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
